<template>
  <a-layout-header class="header">
    <div class="nav-container">
      <!-- 普通用户菜单 -->
      <div class="nav-left">
        <router-link class="logo" to="/">
          <a-typography-text strong class="logo-text">FT Database</a-typography-text>
        </router-link>
        <a-menu v-if="menuVisible && hasUserNavMenu" mode="horizontal" :selectedKeys="[$route.path]" class="nav-menu"
          :items="userNavMenuItems" />
      </div>

      <!-- 管理菜单 -->
      <div v-if="menuVisible && hasManagementMenu" class="management-nav">
        <a-tag :color="modeTagColor" class="mode-tag">{{ modeTagText }}</a-tag>
        <a-menu mode="horizontal" :selectedKeys="[$route.path]" class="nav-menu management-menu"
          :class="{ 'is-ugj': isUGJMode, 'is-ftd': isFTDMode }" :items="managementMenuItems" />
      </div>

      <div class="nav-right">
        <a-space size="middle">
          <template v-if="authStore.isAuthenticated">
            <a-dropdown :menu="{ items: userMenuItems, onClick: handleUserMenuClick }" @openChange="handleDropdownOpen"
              :trigger="['click']">
              <a class="user-link" @click.prevent>
                <a-space size="small">
                  <a-badge :dot="messageStore.unreadCount > 0">
                    <a-avatar size="small">
                      <template #icon>
                        <UserOutlined />
                      </template>
                    </a-avatar>
                  </a-badge>
                  <span class="user-name">{{ authStore.user?.name }}</span>
                  <DownOutlined class="down-icon" />
                </a-space>
              </a>
            </a-dropdown>
          </template>
          <template v-else>
            <router-link to="/login">
              <a-button type="primary" ghost size="small">登录</a-button>
            </router-link>
          </template>
        </a-space>
      </div>
    </div>
  </a-layout-header>

  <!-- 退出确认对话框 -->
  <a-modal v-model:open="showLogoutConfirm" title="确认退出" @ok="handleLogout" @cancel="showLogoutConfirm = false"
    okText="退出" cancelText="取消">
    <p>
      <ExclamationCircleOutlined /> 确定要退出登录吗？
    </p>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed, h } from "vue";
import { useRouter, RouterLink } from "vue-router";
import { useAuthStore } from "@stores/auth";
import { useMessageStore } from "@stores/message";
import { Space, Badge } from 'antdv-next';
import type { MenuProps } from 'antdv-next';
import {
  DownOutlined,
  UserOutlined,
  AccountBookOutlined,
  LogoutOutlined,
  ExclamationCircleOutlined,
} from "@antdv-next/icons";
import {
  isFTDMode,
  isUGJMode,
  showInterviewFeatures,
  showResumeFeatures,
} from "@utils/productMode";

const router = useRouter();
const authStore = useAuthStore();
const messageStore = useMessageStore();
const showLogoutConfirm = ref(false);
const menuVisible = ref(true);



// 普通导航菜单项
const userNavMenuItems = computed<MenuProps['items']>(() => {
  const items: MenuProps['items'] = [
    {
      key: '/search',
      label: h(RouterLink, { to: '/search' }, () => '搜索人才')
    }
  ];

  if (authStore.isAuthenticated) {
    items.push({ key: '/positions', label: h(RouterLink, { to: '/positions' }, () => '我的职位') });

    const talentChildren: NonNullable<MenuProps['items']> = [
      { key: '/candidates', label: h(RouterLink, { to: '/candidates' }, () => '浏览过的人才') },
      { key: '/favorites', label: h(RouterLink, { to: '/favorites' }, () => '职位收藏的人才') },
    ];
    if (showInterviewFeatures) {
      talentChildren.push({
        key: '/interview-invitations',
        label: h(RouterLink, { to: '/interview-invitations' }, () => '邀约面试人才')
      });
    }
    if (showResumeFeatures) {
      talentChildren.push({
        key: '/resume-invitations',
        label: h(RouterLink, { to: '/resume-invitations' }, () => '邀约简历人才')
      });
    }

    items.push(
      {
        key: 'my-browse',
        label: h('span', { class: 'submenu-entry-label' }, [
          h('span', null, '我的人才'),
          h(DownOutlined, { class: 'submenu-entry-icon' }),
        ]),
        children: talentChildren
      },
      { key: '/messages', label: h(RouterLink, { to: '/messages' }, () => '我的消息') }
    );
  }

  return items;
});

// 管理菜单项
const managementMenuItems = computed<MenuProps['items']>(() => {
  const items: MenuProps['items'] = [];

  if (authStore.isAdmin) {
    items.push(
      { key: '/position-management', label: h(RouterLink, { to: '/position-management' }, () => '职位管理') },
      { key: '/profile-pool-management', label: h(RouterLink, { to: '/profile-pool-management' }, () => '人才池管理') },
      { key: '/user-management', label: h(RouterLink, { to: '/user-management' }, () => '用户管理') },
      { key: '/points-management', label: h(RouterLink, { to: '/points-management' }, () => '积分管理') },
      { key: '/system-logs-management', label: h(RouterLink, { to: '/system-logs-management' }, () => '日志管理') }
    );
  } else if (authStore.isStaff) {
    items.push(
      { key: '/position-management', label: h(RouterLink, { to: '/position-management' }, () => '职位管理') },
      { key: '/user-management', label: h(RouterLink, { to: '/user-management' }, () => '用户管理') },
      { key: '/points-management', label: h(RouterLink, { to: '/points-management' }, () => '积分管理') },
      { key: '/invitation-management', label: h(RouterLink, { to: '/invitation-management' }, () => '邀约管理') }
    );
  }

  return items;
});

const hasUserNavMenu = computed(() => (userNavMenuItems.value?.length || 0) > 0);
const hasManagementMenu = computed(() => (managementMenuItems.value?.length || 0) > 0);

// 用户下拉菜单项
const userMenuItems = computed<MenuProps['items']>(() => [
  {
    key: '/user/center',
    label: h('div', { style: { display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: '1.4', padding: '4px 0' } }, [
      h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } }, [
        h(AccountBookOutlined),
        h('span', null, '用户中心'),
      ]),
      h(
        'small',
        { style: { color: 'rgba(0,0,0,0.45)', fontSize: '11px', paddingLeft: '22px' } },
        `${authStore.user?.points || 0} 可用${(authStore.user?.points_pending || 0) > 0 ? ` / ${authStore.user?.points_pending || 0} 待生效` : ''}`
      )
    ])
  },
  {
    key: '/messages',
    label: h(Space, null, {
      default: () => [
        h(AccountBookOutlined),
        h('span', null, [
          '我的消息',
          messageStore.unreadCount > 0 ? h(Badge, { count: messageStore.unreadCount, overflowCount: 99, style: { marginLeft: '8px' } }) : null
        ])
      ]
    })
  },
  { type: 'divider' },
  {
    key: 'logout',
    label: h(Space, null, {
      default: () => [h(LogoutOutlined), h('span', null, '退出登录')]
    })
  }
]);

const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
  if (key === 'logout') {
    confirmLogout();
  } else if (typeof key === 'string' && key.startsWith('/')) {
    router.push(key);
  }
};

const confirmLogout = (): void => {
  showLogoutConfirm.value = true;
};

const handleLogout = async (): Promise<void> => {
  try {
    await authStore.logout();
    showLogoutConfirm.value = false;
    router.push("/login");
  } catch (error) {
    // 即使发生错误也清除本地状态
    authStore.clearAuth();
    showLogoutConfirm.value = false;
    router.push("/login");
  }
};

const handleDropdownOpen = (open: boolean) => {
  if (open) {
    authStore.fetchUser();
  }
};

const modeTagText = computed(() => {
  if (isUGJMode) return 'UGJ'
  if (isFTDMode) return 'FTD'
  return 'DEV'
})

const modeTagColor = computed(() => {
  if (isUGJMode) return 'purple'
  if (isFTDMode) return 'orange'
  return 'gold'
})

// 监听认证状态变化
watch(
  () => authStore.isAuthenticated,
  (newValue: boolean) => {
    // 当认证状态变化时先隐藏菜单，然后通过延迟重新显示来强制刷新
    if (newValue) {
      // 登录成功后，更新未读消息数量
      messageStore.updateUnreadCount();
      authStore.fetchUser();

      menuVisible.value = false;
      setTimeout(() => {
        menuVisible.value = true;
      }, 100);
    }
  },
  { immediate: true }
);

// 组件挂载时确保菜单可见
onMounted(() => {
  menuVisible.value = true;
});
</script>

<style lang="scss" scoped>
.header {
  background: #fff;
  padding: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
  height: 56px;
  line-height: 56px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-left {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.management-nav {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 24px;
}

.logo {
  display: flex;
  align-items: center;
  margin-right: 32px;
  text-decoration: none;

  .logo-text {
    font-size: 18px;
    color: #1677ff;
    white-space: nowrap;
  }
}

.nav-menu {
  border-bottom: none;
  background: transparent;
  flex: 1;
  min-width: 0;
}

.management-menu {
  flex: none;
  min-width: auto;
}

:deep(.management-menu.ant-menu-horizontal) {
  line-height: 56px;
}

/* FTD 模式下的管理菜单主题色 (暖橙色 #fa8c16) */
:deep(.management-menu.is-ftd.ant-menu-horizontal > .ant-menu-item),
:deep(.management-menu.is-ftd.ant-menu-horizontal > .ant-menu-submenu) {
  color: #fa8c16;
}

:deep(.management-menu.is-ftd.ant-menu-horizontal > .ant-menu-item:hover),
:deep(.management-menu.is-ftd.ant-menu-horizontal > .ant-menu-submenu:hover),
:deep(.management-menu.is-ftd.ant-menu-horizontal > .ant-menu-item-active),
:deep(.management-menu.is-ftd.ant-menu-horizontal > .ant-menu-submenu-active) {
  color: #fa8c16;
}

:deep(.management-menu.is-ftd.ant-menu-horizontal > .ant-menu-item-selected),
:deep(.management-menu.is-ftd.ant-menu-horizontal > .ant-menu-submenu-selected) {
  color: #fa8c16;
}

:deep(.management-menu.is-ftd.ant-menu-horizontal > .ant-menu-item-selected::after),
:deep(.management-menu.is-ftd.ant-menu-horizontal > .ant-menu-submenu-selected::after) {
  border-bottom-color: #fa8c16 !important;
}

/* UGJ 模式下的管理菜单主题色 (高级紫 #722ed1) */
:deep(.management-menu.is-ugj.ant-menu-horizontal > .ant-menu-item),
:deep(.management-menu.is-ugj.ant-menu-horizontal > .ant-menu-submenu) {
  color: #722ed1;
}

:deep(.management-menu.is-ugj.ant-menu-horizontal > .ant-menu-item:hover),
:deep(.management-menu.is-ugj.ant-menu-horizontal > .ant-menu-submenu:hover),
:deep(.management-menu.is-ugj.ant-menu-horizontal > .ant-menu-item-active),
:deep(.management-menu.is-ugj.ant-menu-horizontal > .ant-menu-submenu-active) {
  color: #722ed1;
}

:deep(.management-menu.is-ugj.ant-menu-horizontal > .ant-menu-item-selected),
:deep(.management-menu.is-ugj.ant-menu-horizontal > .ant-menu-submenu-selected) {
  color: #722ed1;
}

:deep(.management-menu.is-ugj.ant-menu-horizontal > .ant-menu-item-selected::after),
:deep(.management-menu.is-ugj.ant-menu-horizontal > .ant-menu-submenu-selected::after) {
  border-bottom-color: #722ed1 !important;
}

/* 默认管理菜单主题色 (开发环境 Fallback 黄色 #d46b08) */
:deep(.management-menu.ant-menu-horizontal > .ant-menu-item),
:deep(.management-menu.ant-menu-horizontal > .ant-menu-submenu) {
  color: #d46b08;
}

:deep(.management-menu.ant-menu-horizontal > .ant-menu-item:hover),
:deep(.management-menu.ant-menu-horizontal > .ant-menu-submenu:hover),
:deep(.management-menu.ant-menu-horizontal > .ant-menu-item-active),
:deep(.management-menu.ant-menu-horizontal > .ant-menu-submenu-active) {
  color: #d46b08;
}

:deep(.management-menu.ant-menu-horizontal > .ant-menu-item-selected),
:deep(.management-menu.ant-menu-horizontal > .ant-menu-submenu-selected) {
  color: #d46b08;
}

:deep(.management-menu.ant-menu-horizontal > .ant-menu-item-selected::after),
:deep(.management-menu.ant-menu-horizontal > .ant-menu-submenu-selected::after) {
  border-bottom-color: #d46b08 !important;
}

:deep(.submenu-entry-label) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

:deep(.submenu-entry-icon) {
  font-size: 9px !important;
  transition: transform 0.2s ease, color 0.2s ease;
}

:deep(.ant-menu-submenu:hover .submenu-entry-icon) {
  color: rgba(0, 0, 0, 0.65);
}

.user-link {
  color: rgba(0, 0, 0, 0.88);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.3s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .user-name {
    font-size: 14px;
    font-weight: 500;
  }

  .down-icon {
    font-size: 10px;
    color: rgba(0, 0, 0, 0.45);
  }
}
</style>
